/**
 * The catalog machinery — **Domain A** of `docs/i18n-design.md` §2, which asks
 * for "ordinary i18n. ICU MessageFormat catalogs per locale, `Intl` for
 * dates/money everywhere, locale negotiated browser → user preference → tenant
 * default. Ship EN + ES catalogs; **the framework is the point**."
 *
 * This is the framework, and it is about sixty lines, on purpose.
 *
 * ## Why this is a copy of `apps/site/src/messages/format.ts`
 *
 * Because apps do not cross-import (dev.md §3): `apps/site` is a bucket bundle
 * served on a hundred tenant hostnames and this is an installed PWA on a
 * counter, and the day one of them needs a third plural category or a different
 * `Entry` shape is the day a shared copy would have to grow a flag. Sixty lines
 * duplicated is cheaper than a package that exists to hold sixty lines, and the
 * duplication is *deliberate* rather than an oversight — which is why it is
 * written down here rather than left for somebody to "fix".
 *
 * ## Why there is no i18n library
 *
 * Three reasons, in the order they mattered.
 *
 * **The bundle.** This one is installed on an iPad that is expected to open in a
 * tunnel (spec §8). `intl-messageformat` and a loader are tens of kilobytes of
 * service-worker precache to render sentences we wrote ourselves.
 *
 * **The compiler already does the hard part.** The thing an i18n library
 * actually buys is *safety* — a missing key, a wrong placeholder — and it buys
 * it at runtime, as a fallback or a `[object Object]` on a till. Here `es` is
 * declared `: Messages`, so a missing key is a **compile error** and a
 * placeholder with the wrong shape is a compile error, before anything ships.
 * That is strictly better than any runtime check.
 *
 * **ICU's plural syntax is a second language to be wrong in.** A catalog entry
 * here is a plain string when it takes nothing and a **function** when it takes
 * something, which is the same decision
 * `apps/notifications/src/sms/templates.ts` argued for the OTP text: "'minuto/
 * minutos' is the only agreement this sentence needs, and it is the reason the
 * renderer is a function rather than a string with placeholders". Spanish
 * agreement, English's irregular plurals and the `p. m.` full stops are all
 * ordinary TypeScript here, read and tested like any other code.
 *
 * What is deliberately kept from ICU's model: **the message decides**, not the
 * call site. A component asks for `t('tasks.heading', { count })` and never
 * assembles a sentence out of fragments, because word order is exactly what a
 * translation changes. The two or three places that *do* splice — a sentence
 * with a `<strong>` or a `<code>` inside it — say so where they do it.
 */

/**
 * One entry. A bare string takes nothing; a function takes exactly what its
 * own parameter object declares, and every locale must declare the same one.
 *
 * `never` as the parameter type is what makes the constraint accept any
 * concrete parameter object contravariantly — it is a bound, not a value that
 * ever reaches a template.
 */
export type Entry = string | ((params: never) => string);

export type Catalog = Readonly<Record<string, Entry>>;

/** `[]` for a plain string, `[TheParams]` for a function — so `t` needs no overloads. */
export type ParamsOf<C, K extends keyof C> = C[K] extends (params: infer P) => string ? [P] : [];

export interface Translate<C extends Catalog> {
  <K extends keyof C>(key: K, ...params: ParamsOf<C, K>): string;
}

/**
 * Bind a catalog to a `t`.
 *
 * There is no fallback chain and no "missing key" branch, because there cannot
 * be a missing key: every catalog is checked against the English one at compile
 * time. A runtime fallback here would be a place for a Spanish till to quietly
 * print an English sentence, which is precisely the surprise the type is for.
 */
export function translator<C extends Catalog>(catalog: C): Translate<C> {
  return <K extends keyof C>(key: K, ...params: ParamsOf<C, K>): string => {
    const entry: Entry | undefined = catalog[key];
    if (typeof entry === 'function') {
      const render = entry as (p: unknown) => string;
      return render(params[0]);
    }
    // `entry` cannot actually be `undefined` — `K extends keyof C` — but the
    // index signature on `Catalog` says it might, and asserting it away would be
    // the one place a real missing key could hide. An empty string on screen is
    // louder than a crash on a counter and quieter than a lie.
    return entry ?? '';
  };
}

/**
 * The two-form cardinal split, for the languages that have one.
 *
 * English and Spanish both distinguish exactly *one* from *everything else*
 * (`0 nights`, `0 noches`), so one helper serves both catalogs. It lives here
 * rather than in either of them because a helper per language that did the same
 * thing would drift.
 *
 * A language with more categories — Polish has three, Arabic six — does **not**
 * extend this function. It writes its own arithmetic inside its own catalog
 * entries, which is the whole reason an entry is a function: the plural rule
 * belongs to the sentence, in the language the sentence is in.
 */
export function plural(count: number, one: string, other: string): string {
  return count === 1 ? one : other;
}
